import { parseISO } from 'date-fns';
import { PAYMENT_STATUS, User } from '../typesAndInterfaces/typesAndInterfaces';

export type AccessUser = Pick<User, 'paymentStatus' | 'manualPremium' | 'manualPremiumExpireDate' | 'accessExpiresAt' | 'grandfathered' | 'educatorCode'>;

export type UserAccess = {
  canAccessRooms: boolean;
  canAccessGames: boolean;
};

/**
 * Single source of truth for whether a user has premium access — a direct mirror
 * of the backend `useGetAccess` (do not let these diverge).
 *
 * A user has access when ANY of these holds:
 *  - grandfathered lifetime grant, OR
 *  - manualPremium is set AND still active (no expiry = lifetime, or a future
 *    manualPremiumExpireDate — e.g. a 1-year off-app/web payment), OR
 *  - paymentStatus is `paid` AND the paid entitlement is not expired: legacy
 *    one-time 'premium' has no `accessExpiresAt` (lifetime), while an annual
 *    subscription (premium_annual) is active only while now < accessExpiresAt, OR
 *  - a valid (non-expired) educator/redeem code.
 *
 * `accessExpiresAt`/`grandfathered` are server-computed (getUserDataForClient).
 * When a subscription expires, hasActivePaid flips false and premium content
 * routes to the PaymentPage (the renewal prompt).
 *
 * Kept free of React/React-Native imports so it can be unit-tested directly and
 * shared by hooks and screens.
 */
export function getUserAccess(user?: AccessUser): UserAccess {
  const educatorCode = user?.educatorCode;
  const isEducatorCodeValid = !!educatorCode && Date.now() < parseISO(educatorCode.expireDate).getTime();

  const hasActiveManualPremium = user?.grandfathered === true
    || (user?.manualPremium === true
      && (!user.manualPremiumExpireDate || Date.now() < parseISO(user.manualPremiumExpireDate).getTime()));

  const hasActivePaid = user?.paymentStatus === PAYMENT_STATUS.paid
    && (!user.accessExpiresAt || Date.now() < parseISO(user.accessExpiresAt).getTime());

  const hasAccess = hasActivePaid || hasActiveManualPremium || isEducatorCodeValid;

  return {
    canAccessRooms: hasAccess,
    canAccessGames: hasAccess,
  };
}
